import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Eye, EyeOff, User, Briefcase } from 'lucide-react';

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/bbyq8lme_icone_verre_vert_512.png";

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPro, setIsPro] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    prenom: '',
    nom: '',
    telephone: '',
    raison_sociale: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister
        ? {
            email: formData.email,
            password: formData.password,
            prenom: isPro ? '' : formData.prenom,
            nom: isPro ? '' : formData.nom,
            telephone: formData.telephone,
            is_pro: isPro,
            raison_sociale: isPro ? formData.raison_sociale : null,
          }
        : {
            email: formData.email,
            password: formData.password,
          };

      const response = await apiClient.post(endpoint, payload);
      onLogin(response.data.user, response.data.token);
      toast.success(isRegister ? 'Inscription réussie !' : 'Connexion réussie !');
      // Rediriger les nouveaux inscrits vers l'activation de l'essai
      navigate(isRegister ? '/activer-essai' : '/');
    } catch (error) {
      const message = getErrorMessage(error, 'Une erreur est survenue');
      if (message) toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Image Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 items-center justify-center">
        <div className="text-center p-12 max-w-lg">
          <Link to="/" className="inline-block group">
            <div className="flex items-center justify-center gap-3 mb-8">
              <img 
                src={LOGO_URL} 
                alt="copropriete.app" 
                className="w-16 h-16 rounded-2xl group-hover:scale-110 transition-transform duration-300"
              />
              <span className="text-3xl font-bold text-white">copropriete.app</span>
            </div>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-4">
            Reprenez le contrôle de votre copropriété
          </h1>
          <p className="text-xl text-emerald-400 font-semibold mb-6">
            100% gratuit, sans engagement
          </p>
          <p className="text-lg text-slate-300">
            Gérez vos assemblées générales, appels de fonds et copropriétaires en toute simplicité.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Header mobile avec logo */}
        <div className="lg:hidden p-4 border-b border-slate-100">
          <Link to="/" className="inline-flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="copropriete.app" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="text-xl font-bold text-slate-900">copropriete.app</span>
          </Link>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-6 md:p-8">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                {isRegister ? 'Créer un compte' : 'Connexion'}
              </h2>
              <p className="text-slate-500 text-sm md:text-base">
                {isRegister
                  ? 'Créez votre compte pour commencer'
                  : 'Accédez à votre espace de gestion'}
              </p>
            </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <>
                {/* Choix Particulier / Pro */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setIsPro(false)}
                    data-testid="select-particulier"
                    className={`p-4 rounded-xl border-2 transition-all ${
                      !isPro 
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                  >
                    <User className={`w-6 h-6 mx-auto mb-2 ${!isPro ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <span className="block font-semibold text-sm">Particulier</span>
                    <span className="block text-xs mt-1 opacity-75">Syndic bénévole</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsPro(true)}
                    data-testid="select-pro"
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isPro 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                  >
                    <Briefcase className={`w-6 h-6 mx-auto mb-2 ${isPro ? 'text-blue-600' : 'text-slate-400'}`} />
                    <span className="block font-semibold text-sm">Syndic Pro</span>
                    <span className="block text-xs mt-1 opacity-75">Professionnel</span>
                  </button>
                </div>

                {/* Champs conditionnels */}
                {isPro ? (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Raison sociale *
                    </label>
                    <input
                      type="text"
                      name="raison_sociale"
                      data-testid="register-raison-sociale-input"
                      value={formData.raison_sociale}
                      onChange={handleChange}
                      required
                      placeholder="Nom de votre entreprise"
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        name="prenom"
                        data-testid="register-prenom-input"
                        value={formData.prenom}
                        onChange={handleChange}
                        required={!isPro}
                        placeholder="Jean"
                        className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        name="nom"
                        data-testid="register-nom-input"
                        value={formData.nom}
                        onChange={handleChange}
                        required={!isPro}
                        placeholder="Dupont"
                        className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="telephone"
                    data-testid="register-telephone-input"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                data-testid="login-email-input"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="vous@exemple.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Mot de passe *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  data-testid="login-password-input"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Votre mot de passe"
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid={isRegister ? 'register-submit-button' : 'login-submit-button'}
              disabled={loading}
              className={`w-full py-2.5 rounded-md font-medium transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                isRegister && isPro 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              {loading
                ? 'Chargement...'
                : isRegister
                ? "S'inscrire"
                : 'Se connecter'}
            </button>
          </form>

          <div className="mt-8 text-center">
            {isRegister ? (
              <button
                onClick={() => {
                  setIsRegister(false);
                  setIsPro(false);
                }}
                data-testid="toggle-auth-mode-button"
                className="text-slate-600 hover:text-slate-800 transition-colors"
              >
                Vous avez déjà un compte ? <span className="font-semibold text-slate-900">Connectez-vous</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setIsRegister(true);
                  setIsPro(false);
                }}
                data-testid="toggle-auth-mode-button"
                className="text-slate-600 hover:text-slate-800 transition-colors"
              >
                Pas encore de compte ? <span className="font-semibold text-emerald-600 hover:text-emerald-700">Créer un compte gratuit</span>
              </button>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
